import React, { useState, useRef, useCallback, useEffect } from 'react';
import Select, { createFilter } from 'react-select';
import { FixedSizeList } from 'react-window';
import { CFormFeedback, CFormInput, CFormLabel, CPopover } from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import classNames from 'classnames';

import { useOutsideHandling } from '../../../utils';
import { DEFAULT_FN } from '../../../constants';

const selectStyles = {
     control: (provided) => ({ ...provided, minWidth: 240, margin: 8 }),
     menu: () => ({ boxShadow: 'inset 0 1px 0 rgba(0, 0, 0, 0.1)' }),
     menuList: (base) => ({ ...base, height: 150 }),
};

const selectStylesNoHeight = {
     control: (provided) => ({ ...provided, minWidth: 240, margin: 8 }),
     menu: () => ({ boxShadow: 'inset 0 1px 0 rgba(0, 0, 0, 0.1)' }),
};

const MenuList = (props) => {
     const { options, children, maxHeight, getValue } = props;

     const [value] = getValue();
     const height = 35;
     const initialOffset = options.indexOf(value) * height;

     let newMaxHeight = maxHeight;

     return (
          <FixedSizeList height={newMaxHeight} itemCount={children.length} itemSize={height} initialScrollOffset={initialOffset}>
               {({ index, style }) => <div style={style}>{children[index]}</div>}
          </FixedSizeList>
     );
};

const Dropdown = ({ children, disabled, isOpen, target, onClose, invalid, setIsOpen }) => {
     const handleFocus = () => {
          setIsOpen(true);
     };

     const handleBlur = () => {
          setIsOpen(false);
     };

     const handleKeyDown = (event) => {
          if (event.shiftKey && event.key === 'Tab') {
               setIsOpen(false);
          }
     };

     return (
          <div
               className={classNames('select-wrapper', { 'is-invalid': invalid, disabled })}
               tabIndex="0"
               onFocus={handleFocus}
               onKeyDown={handleKeyDown}
          >
               {target}
               {isOpen && <Menu onBlur={handleBlur}>{children}</Menu>}
               {isOpen && <Blanket onClick={onClose} />}
          </div>
     );
};

const Menu = (props) => {
     return <div className="select-search-list" {...props} />;
};

const Blanket = (props) => (
     <div
          style={{
               bottom: 0,
               left: 0,
               top: 0,
               right: 0,
               position: 'absolute',
               zIndex: 1,
          }}
          {...props}
     />
);

const SelectSearchField = ({
     id,
     placeholder,
     options,
     onChange,
     isRequired,
     isValidate,
     label,
     value,
     error,
     disabled,
     className,
     classNamePrefix,
     classNameInput,
     classNameWrap,
     selectInputType = true,
     setListOption = DEFAULT_FN,
     tooltip,
     placeholderTextSearch,
     height,
     useOriginalArrow,
}) => {
     const wrapperRef = useRef(null);
     const preventClickOutsideRef = useRef(false);

     const [isOpen, setIsOpen] = useState(false);
     const [initValue, setInitValue] = useState(value);

     const inputValue = value && initValue && initValue.label ? initValue.label : placeholder;
     const lengthError = error && Object.keys(error).length;

     if (lengthError === 0) {
          error = false;
     }

     const componentRender =
          options?.length > 8
               ? {
                      ClearIndicator: null,
                      MenuList,
                 }
               : {
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null,
                 };

     useEffect(() => {
          setInitValue(value);
     }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

     const clickOutside = useCallback(() => {
          if (preventClickOutsideRef.current) return;

          setIsOpen(false);
     }, []);

     useOutsideHandling(wrapperRef, clickOutside);

     const toggleOpen = () => {
          setListOption();

          const toggleOpenTimer = setTimeout(() => {
               setIsOpen(!isOpen);

               clearTimeout(toggleOpenTimer);
          }, 0);
     };

     const onSelectChange = (e) => {
          toggleOpen();
          setInitValue(e);
          onChange(e);
     };

     const handleClickSelectSearchField = (e) => {
          toggleOpen();
     };

     return (
          <div ref={wrapperRef} className={classNames('form-group select-react-customize', { [classNameWrap]: classNameWrap })}>
               {label && (
                    <CFormLabel htmlFor={id}>
                         {<span className="me-1">{label}</span>}
                         {isRequired && <span className="text-danger">*</span>}
                         {tooltip && (
                              <CPopover content={tooltip} placement="top" trigger="hover">
                                   <CIcon icon="icon-info-cilcle" />
                              </CPopover>
                         )}
                    </CFormLabel>
               )}
               <Dropdown
                    disabled={disabled}
                    setIsOpen={!disabled ? setIsOpen : DEFAULT_FN}
                    isOpen={isOpen}
                    onClose={toggleOpen}
                    invalid={(isRequired || isValidate) && !!error}
                    target={
                         <div className="select-target-group">
                              {selectInputType ? (
                                   <CFormInput
                                        readOnly
                                        value={inputValue}
                                        disabled={disabled}
                                        invalid={(isRequired || isValidate) && !!error}
                                        onClick={handleClickSelectSearchField}
                                   />
                              ) : (
                                   <div className="form-group form-group-country">
                                        <div
                                             className={classNames('stretch-container form-control dropdown', {
                                                  [classNameInput]: classNameInput,
                                                  'dropdown-custom': !disabled,
                                                  'arrow-original': useOriginalArrow,
                                                  'is-invalid': (isRequired || isValidate) && !!error,
                                                  disabled,
                                             })}
                                        >
                                             <p className="text-one-line m-0">{inputValue}</p>
                                        </div>
                                   </div>
                              )}
                         </div>
                    }
               >
                    <Select
                         id={id}
                         autoFocus
                         backspaceRemovesValue={false}
                         components={componentRender}
                         controlShouldRenderValue={false}
                         hideSelectedOptions={false}
                         isClearable={false}
                         menuIsOpen
                         onChange={onSelectChange}
                         options={options}
                         filterOption={createFilter({
                              matchFrom: 'any',
                              stringify: (option) => `${option.label}`,
                         })}
                         placeholder={placeholderTextSearch || 'Select...'}
                         styles={height ? selectStyles : selectStylesNoHeight}
                         tabSelectsValue={false}
                         value={initValue}
                         className={classNames({ [className]: className }, 'select-search-box')}
                         classNamePrefix={classNamePrefix}
                         menuShouldScrollIntoView
                         isDisabled={disabled}
                    />
               </Dropdown>
               {(isRequired || isValidate) && typeof error === 'string' && <CFormFeedback invalid>{error}</CFormFeedback>}
          </div>
     );
};

export default SelectSearchField;
